import { useState } from 'react';
import { Card, Button, Alert, Tag, Input, Popconfirm, Space, Tooltip, message } from '../../ui';
import { Lock, Percent, CheckCircle2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { useAuthStore } from '../../store/auth';

const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Advance / prepaid cash-discount panel on the Order detail page. The CD is applied AUTOMATICALLY for
 * every eligible order — chosen at placement (ESTIMATED), locked the moment a verified payment covers the
 * net, and dropped at billing if none does. This panel shows that state + keeps manual overrides:
 *  - ESTIMATED → the pending benefit (taxable CD + GST reduction); Accounts/Admin/SA may manually Lock
 *    (or Admin/SA override-lock without full payment) or Clear it, but normally it locks on its own.
 *  - LOCKED    → the applied discount summary (who/when + audit remarks).
 *  - NONE + eligible (staff, before billing) → a re-apply shortcut (rare — new orders auto-choose).
 * Manual locking requires an approved payment covering the net payable; Admin/SA may override with remarks.
 * The whole panel disappears once a Tally invoice exists (frozen) or the order can no longer take a CD.
 */
export function AdvanceCdPanel({
  orderId, orderStatus, canLock, canChoose,
}: {
  orderId: string;
  orderStatus: string;
  canLock: boolean;   // ORDER.CD.LOCK
  canChoose: boolean; // ORDER.PAY (choose/clear the offer)
}) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const isAdminSa = role === 'ADMIN' || role === 'SUPER_ADMIN'; // only these may override the payment gate (backend-enforced)
  // The viewer is the buyer (retailer/farmer), not internal staff — drives "you saved" vs "the dealer saved"
  // wording and hides internal jargon (rule derivation, Tally, audit trail) from the customer.
  const isCustomer = role === 'RETAILER' || role === 'FARMER';
  // Sales staff (Sales Person / Sales Head) have NO authority over discounts — they may VIEW the
  // advance-CD panel but cannot apply (choose) or clear it. Backend also enforces this (403). The
  // choose/clear actions remain for the dealer (own decision) and Accounts/Admin/SA.
  const canAct = canChoose && role !== 'SALES_PERSON' && role !== 'SALES_HEAD';
  const [override, setOverride] = useState(false);
  const [remarks, setRemarks] = useState('');

  const { data: cd } = useQuery({
    queryKey: ['advance-cd', orderId],
    queryFn: () => request<any>({ url: `/orders/${orderId}/advance-cd` }),
    enabled: !!orderId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['advance-cd', orderId] });
    qc.invalidateQueries({ queryKey: ['order', orderId] });
    qc.invalidateQueries({ queryKey: ['order-pay-summary', orderId] });
    qc.invalidateQueries({ queryKey: ['order-timeline', orderId] });
  };
  const onErr = (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed');

  const choose = useMutation({
    mutationFn: () => request({ url: `/orders/${orderId}/advance-cd/choose`, method: 'POST' }),
    onSuccess: () => { message.success('Advance cash discount applied — it locks automatically once a verified payment covers the net.'); refresh(); },
    onError: onErr,
  });
  const clear = useMutation({
    mutationFn: () => request({ url: `/orders/${orderId}/advance-cd/clear`, method: 'POST' }),
    onSuccess: () => { message.success('Advance cash discount cleared — order bills at the original amount.'); refresh(); },
    onError: onErr,
  });
  const lock = useMutation({
    mutationFn: () => request({ url: `/orders/${orderId}/advance-cd/lock`, method: 'POST', data: { override, remarks: remarks.trim() || undefined } }),
    onSuccess: () => { message.success('Advance cash discount locked — the order now bills at the net amount.'); setOverride(false); setRemarks(''); refresh(); },
    onError: onErr,
  });

  if (!cd) return null;
  const status: 'NONE' | 'ESTIMATED' | 'LOCKED' = cd.status;
  // A frozen order (Tally invoice on file) or a terminal order shows only the locked summary (if any).
  const terminal = ['CANCELLED', 'REJECTED', 'CLOSED'].includes(orderStatus);
  if (status === 'NONE' && (!cd.eligible || cd.frozen || terminal || !canAct)) return null;

  // "You saved" = original grand − net payable. Its parts are the CD on goods + the GST it removes, PLUS
  // the whole-rupee round-off on the net (rounding the net DOWN means you pay a few paise less). Fold the
  // round-off term in so the parenthetical actually sums to the headline figure — no silent 0.10 gap.
  const r2c = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
  const netGstTop = r2c(Number(cd.net?.cgst ?? 0) + Number(cd.net?.sgst ?? 0) + Number(cd.net?.igst ?? 0));
  const roundSaving = r2c((Number(cd.net?.taxable ?? 0) + netGstTop) - Number(cd.netPayable ?? 0));
  const gstText = `${inr2(cd.cdAmount)} taxable + ${inr2(cd.gstReduction)} GST${Math.abs(roundSaving) >= 0.005 ? ` ${roundSaving >= 0 ? '+' : '−'} ${inr2(Math.abs(roundSaving))} round-off` : ''}`;
  const covered = !!cd.paymentCovered;
  const needsOverride = !covered; // no verified payment covering the net payable yet
  // Lock is blocked when the net isn't covered unless an Admin/SA overrides with remarks.
  const overrideReady = override && remarks.trim().length >= 3;
  const lockBlocked = needsOverride && !(isAdminSa && overrideReady);

  // ── LOCKED ────────────────────────────────────────────────────────────────
  if (status === 'LOCKED') {
    return (
      <Card style={{ marginBottom: 12, borderColor: '#b7eb8f' }}
        title={<span><Lock size={16} style={{ color: '#389e0d' }} /> Advance cash discount — locked</span>}>
        <Alert type="success" style={{ marginBottom: 10 }}
          title={`${cd.pct}% cash discount applied${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} — ${isCustomer ? 'you saved' : 'the dealer saved'} ${inr2(cd.totalBenefit)} (${gstText}).`} />
        <Breakdown cd={cd} isCustomer={isCustomer} />
        {canLock && (
          <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
            Locked{cd.lockedByName ? ` by ${cd.lockedByName}` : ''}{cd.lockedAt ? ` · ${new Date(cd.lockedAt).toLocaleString('en-IN')}` : ''}.
            {cd.remarks ? <> Remarks: <i>{cd.remarks}</i></> : null}
          </div>
        )}
      </Card>
    );
  }

  // ── ESTIMATED ───────────────────────────────────────────────────────────────
  if (status === 'ESTIMATED') {
    return (
      <Card style={{ marginBottom: 12, borderColor: '#ffd591' }}
        title={<span><Percent size={16} style={{ color: '#d46b08' }} /> Advance cash discount — pending verification</span>}
        extra={<Tag tone="gold">ESTIMATED</Tag>}>
        <Alert type="warning" style={{ marginBottom: 10 }}
          title={!isCustomer
            ? `Advance CD benefit${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} pending verification / lock — ${inr2(cd.totalBenefit)} (${gstText}).`
            : `Advance cash discount${cd.appliedLabel ? ` (${cd.appliedLabel})` : ''} pending verification — you save ${inr2(cd.totalBenefit)} (${gstText}).`}
          description={!isCustomer
            ? <span>Net payable <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). It locks <b>automatically</b> once a verified payment covers the net; if none is received by billing, the order bills at {inr2(cd.original.grand)}.</span>
            : <span>Net payable <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}). Pay the net before billing to claim this discount — it applies automatically once your payment is verified.</span>} />
        <Breakdown cd={cd} isCustomer={isCustomer} />

        {cd.frozen ? (
          <Alert style={{ marginTop: 10 }} type="info" title="A Tally invoice already exists — the advance CD is frozen and can no longer be changed." />
        ) : (
          <>
            {/* Lock verification is an Accounts/Admin/SA concern — only they see the payment-coverage line
                + override + Lock button. Everyone else (e.g. the retailer viewing their order) gets a plain
                read-only status instead of controls they can't use. */}
            {canLock ? (
              <div style={{ marginTop: 10, fontSize: 13 }}>
                {covered
                  ? <span style={{ color: '#389e0d' }}><CheckCircle2 size={16} /> Verified payment {inr2(cd.approvedPaid)} covers the net payable — it locks automatically on payment approval; you can also lock it now.</span>
                  : <span style={{ color: '#d46b08' }}>Verified payment {inr2(cd.approvedPaid)} does not yet cover the net payable {inr2(cd.netPayable)}. It locks automatically once a covering payment is verified; to lock now an Admin/SA override with audit remarks is required.</span>}
              </div>
            ) : (
              <div style={{ marginTop: 10, fontSize: 13, color: '#8c8c8c' }}>
                This discount applies automatically once a verified payment covers the net (before billing). If none is received by billing, the order bills at the original amount.
              </div>
            )}

            {canLock && needsOverride && isAdminSa && (
              <div style={{ marginTop: 10, padding: 10, background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} />
                  Override — lock without a verified payment covering the net (Admin/SA only)
                </label>
                {override && (
                  <Input.TextArea style={{ marginTop: 8 }} rows={2} value={remarks} maxLength={500}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Mandatory audit remarks — why is this being locked without full verified payment?" />
                )}
              </div>
            )}

            {(canLock || canAct) && (
              <Space style={{ marginTop: 12 }}>
                {canLock && (
                  <Popconfirm
                    title="Lock the advance cash discount?"
                    description={`The order will bill at ${inr2(cd.netPayable)} (net). This cannot be undone once a Tally invoice is raised.`}
                    okText="Lock"
                    disabled={lockBlocked}
                    onConfirm={() => lock.mutate()}
                  >
                    <Tooltip label={lockBlocked
                      ? (isAdminSa
                          ? 'Tick override and enter audit remarks (min 3 chars) to lock without full verified payment.'
                          : `Record & verify a payment covering ${inr2(cd.netPayable)} first — only an Admin/SA can lock without it.`)
                      : ''}>
                      <Button variant="primary" icon={<Lock size={16} />} loading={lock.isPending} disabled={lockBlocked}>
                        Lock discount
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                )}
                {canAct && (
                  <Popconfirm title="Clear the advance cash discount?" description="The order reverts to the original amount." okText="Clear"
                    onConfirm={() => clear.mutate()}>
                    <Button variant="danger" loading={clear.isPending}>Clear</Button>
                  </Popconfirm>
                )}
              </Space>
            )}
          </>
        )}
      </Card>
    );
  }

  // ── NONE but eligible (staff can apply on the dealer's behalf, before billing) ─────────────
  return (
    <Card style={{ marginBottom: 12 }}
      title={<span><Percent size={16} /> Advance cash discount available</span>}>
      <Alert type="info" style={{ marginBottom: 10 }}
        title={isCustomer
          ? `You qualify for a ${cd.pct}% before-billing cash discount — save ${inr2(cd.totalBenefit)} (${gstText}) by paying before billing.`
          : `This dealer qualifies for a ${cd.pct}% before-billing cash discount — a ${inr2(cd.totalBenefit)} benefit (${gstText}) if paid before billing.`}
        description={<span>Net payable would be <b>{inr2(cd.netPayable)}</b> (was {inr2(cd.original.grand)}).</span>} />
      <Breakdown cd={cd} isCustomer={isCustomer} />
      <Popconfirm title="Apply the advance cash discount?" description="Sets the order to the net payable pending payment + lock." okText="Apply"
        onConfirm={() => choose.mutate()}>
        <Button variant="primary" style={{ marginTop: 12 }} icon={<Percent size={16} />} loading={choose.isPending}>
          Apply advance discount
        </Button>
      </Popconfirm>
    </Card>
  );
}

// Full amount trail in two clearly-labelled steps so anyone can follow it: Step 1 builds the original
// grand total (list → standing discount → taxable → GST); Step 2 applies the advance cash discount — and
// spells out that the % is taken on the TAXABLE value (not the grand total) → net payable → saving.
function Breakdown({ cd, isCustomer }: { cd: any; isCustomer?: boolean }) {
  const r2 = (n: number) => Math.round((n || 0) * 100) / 100;
  const origGst = r2((cd.original?.cgst ?? 0) + (cd.original?.sgst ?? 0) + (cd.original?.igst ?? 0));
  const netGst = r2((cd.net?.cgst ?? 0) + (cd.net?.sgst ?? 0) + (cd.net?.igst ?? 0));
  const netRoundOff = r2(Number(cd.netPayable) - (Number(cd.net?.taxable ?? 0) + netGst));
  const hasStandingDisc = cd.originalSubtotal != null && Number(cd.originalDiscount) > 0;
  const stdPct = hasStandingDisc && Number(cd.originalSubtotal) > 0 ? r2((Number(cd.originalDiscount) / Number(cd.originalSubtotal)) * 100) : 0;
  const taxable0 = inr2(cd.original.taxable);
  const cd0 = inr2(cd.cdAmount);
  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden', fontSize: 13 }}>
      {/* ── Step 1: build the original grand total ── */}
      <Section>Step 1 · Order value (before cash discount)</Section>
      {hasStandingDisc && (
        <>
          <Row label="List value (before discount)" value={inr2(cd.originalSubtotal)} />
          <Row label={`Less: standing discount (${stdPct}%)`} value={`− ${inr2(cd.originalDiscount)}`} accent="#cf1322" />
        </>
      )}
      <Row label="Taxable value (goods, before GST)" value={inr2(cd.original.taxable)} />
      <Row label="GST" value={`+ ${inr2(origGst)}`} />
      <Row label="Original grand total" value={<b>{inr2(cd.original.grand)}</b>} strong />

      {/* ── Step 2: apply the advance cash discount ON THE TAXABLE value ── */}
      <Section>Step 2 · Advance cash discount — {Number(cd.pct)}% of the taxable value ({taxable0}), <u>not</u> the grand total</Section>
      <Row label={`Cash discount = ${Number(cd.pct)}% × ${taxable0} (taxable)`} value={`− ${inr2(cd.cdAmount)}`} accent="#cf1322" />
      <Row label={`Net taxable value = ${taxable0} − ${cd0}`} value={inr2(cd.net.taxable)} />
      <Row label="GST on net" value={`+ ${inr2(netGst)}`} />
      {Math.abs(netRoundOff) >= 0.005 && (
        <Row label="Round-off" value={`${netRoundOff < 0 ? '−' : '+'} ${inr2(Math.abs(netRoundOff))}`} />
      )}
      <Row label="Net payable" value={<b>{inr2(cd.netPayable)}</b>} strong />
      <Row label={isCustomer ? 'You save' : 'Dealer saves'} value={<b style={{ color: '#389e0d' }}>{inr2(cd.totalBenefit)}</b>} strong />
    </div>
  );
}

// A muted section-label row that groups the two calculation steps.
function Section({ children }: { children: any }) {
  return (
    <div style={{ padding: '6px 12px', borderTop: '1px solid #f0f0f0', background: '#f6f8fa', fontSize: 11.5, fontWeight: 600, color: '#6b7280' }}>
      {children}
    </div>
  );
}

function Row({ label, value, accent, strong }: { label: string; value: any; accent?: string; strong?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #fafafa', background: strong ? '#fafafa' : undefined }}>
      <span style={{ color: '#595959' }}>{label}</span>
      <span style={{ color: accent, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
